import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { UNSAFE_NavigationContext, useNavigate } from 'react-router-dom';

interface UseUnsavedChangesGuardParams {
  isDirty: boolean;
  onDiscard?: () => void;
}

interface UseUnsavedChangesGuardReturn {
  isModalOpen: boolean;
  handleModalDiscard: () => void;
  handleModalCancel: () => void;
  checkUnsavedChanges: (onProceed: () => void) => boolean;
  /** Call this before programmatic navigation to bypass the guard (e.g., after successful save) */
  allowNavigation: () => void;
}

/**
 * Hook to guard against navigation and page reload when there are unsaved changes
 * Intercepts navigation attempts and browser page reload, showing confirmation modal
 *
 * @param isDirty - Whether the form has unsaved changes
 * @param onDiscard - Optional callback when user discards changes
 *
 * @example
 * ```tsx
 * const { isModalOpen, handleModalDiscard, handleModalCancel, checkUnsavedChanges } =
 *   useUnsavedChangesGuard({
 *     isDirty: form.formState.isDirty,
 *     onDiscard: () => form.reset(),
 *   });
 *
 * // For special cases (like delete with unsaved changes)
 * const handleDelete = () => {
 *   const shouldProceed = checkUnsavedChanges(() => {
 *     // Proceed with delete
 *     setDeleteModalOpen(true);
 *   });
 *
 *   if (shouldProceed) {
 *     // Form is clean, proceed immediately
 *     setDeleteModalOpen(true);
 *   }
 * };
 * ```
 */
export function useUnsavedChangesGuard({
  isDirty,
  onDiscard,
}: UseUnsavedChangesGuardParams): UseUnsavedChangesGuardReturn {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Ref to store callback for special cases (like delete with unsaved changes)
  const pendingActionRef = useRef<(() => void) | null>(null);

  // Flag to allow navigation after discarding changes
  const allowNavigationRef = useRef(false);

  // Store pending navigation info
  const pendingNavigationInfoRef = useRef<{
    method: 'push' | 'replace';
    to: any;
    state?: any;
  } | null>(null);

  // Store latest isDirty value to avoid closure issues
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  // Prevent accidental page reload when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        // Chrome requires returnValue to be set
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Track whether we've pushed a guard history entry
  const hasGuardEntryRef = useRef(false);

  // Push a guard history entry when form becomes dirty (to intercept back button)
  useEffect(() => {
    if (isDirty && !hasGuardEntryRef.current) {
      const fullUrl = window.location.pathname + window.location.search + window.location.hash;
      window.history.pushState({ guardEntry: true }, '', fullUrl);
      hasGuardEntryRef.current = true;
    }
  }, [isDirty]);

  // Block browser back/forward navigation when form is dirty
  useEffect(() => {
    const handlePopState = () => {
      if (isDirtyRef.current && !allowNavigationRef.current) {
        // Push current state back to prevent navigation (preserve full URL)
        const fullUrl = window.location.pathname + window.location.search + window.location.hash;
        window.history.pushState({ guardEntry: true }, '', fullUrl);

        // Show confirmation modal
        setPendingNavigation(null); // We don't know the exact target for back/forward
        setIsModalOpen(true);

        // Store a marker that this was a browser navigation
        pendingNavigationInfoRef.current = { method: 'push', to: -1 }; // -1 indicates back button
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Block navigation when form is dirty using UNSAFE_NavigationContext
  const navigationContext = useContext(UNSAFE_NavigationContext);
  const navigator = navigationContext?.navigator;

  useEffect(() => {
    if (!navigator) return;

    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    // Common navigation handler
    const handleNavigation = (
      method: 'push' | 'replace',
      originalMethod: typeof originalPush,
      to: any,
      state?: any
    ) => {
      // Allow navigation if flag is set or form is not dirty
      if (allowNavigationRef.current || !isDirtyRef.current) {
        allowNavigationRef.current = false;
        originalMethod.call(navigator, to, state);
        return;
      }

      const nextPath = typeof to === 'string' ? to : to.pathname;
      const currentPath = window.location.pathname;

      if (nextPath !== currentPath) {
        setPendingNavigation(nextPath);
        setIsModalOpen(true);
        pendingNavigationInfoRef.current = { method, to, state };
      } else {
        // Same pathname (e.g., query param / hash changes from filters) — allow through
        originalMethod.call(navigator, to, state);
      }
    };

    // Intercept navigation attempts
    navigator.push = (to: any, state?: any) => {
      handleNavigation('push', originalPush, to, state);
    };

    navigator.replace = (to: any, state?: any) => {
      handleNavigation('replace', originalReplace, to, state);
    };

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
    };
  }, [navigator]);

  const handleModalDiscard = useCallback(() => {
    // Call custom discard handler if provided
    onDiscard?.();

    setIsModalOpen(false);

    // Allow navigation for both pending actions and pending navigation
    allowNavigationRef.current = true;

    // If there was a pending action (like delete, tab change, or a programmatic
    // navigation), execute it. Keep `allowNavigationRef` true until the action's
    // navigation settles — some actions navigate via an async popstate
    // (`navigate(-1)`) that the guard would otherwise re-block, leaving the user
    // stranded with a cleared-but-not-navigated form.
    if (pendingActionRef.current) {
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      action();
      // Reset on the next tick, after any popstate from the action has run.
      setTimeout(() => {
        allowNavigationRef.current = false;
      }, 0);
    }

    // If there was a pending navigation, proceed with it preserving method and state
    const pendingInfo = pendingNavigationInfoRef.current;
    if (pendingInfo) {
      allowNavigationRef.current = true;
      const { to } = pendingInfo;

      // Check if this was a browser back/forward button
      if (to === -1) {
        // Navigate back: go(-2) to skip past the guard entry we pushed
        if (hasGuardEntryRef.current) {
          hasGuardEntryRef.current = false;
          window.history.go(-2);
        } else {
          window.history.back();
        }
      } else {
        // Regular navigation
        const { method, state } = pendingInfo;
        navigate(to, { replace: method === 'replace', state });
      }

      setPendingNavigation(null);
      pendingNavigationInfoRef.current = null;
      return;
    }

    // Fallback for legacy string-only navigation (should not occur with new code)
    if (pendingNavigation) {
      allowNavigationRef.current = true;
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [navigate, onDiscard, pendingNavigation]);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setPendingNavigation(null);
    pendingNavigationInfoRef.current = null;
    pendingActionRef.current = null;
  }, []);

  /**
   * Check if there are unsaved changes before proceeding with an action
   * If form is dirty, shows modal and stores the action to execute after discard
   * If form is clean, returns true to proceed immediately
   *
   * @param onProceed - Callback to execute after changes are discarded
   * @returns true if should proceed immediately (form is clean), false if modal is shown
   */
  const checkUnsavedChanges = useCallback((onProceed: () => void): boolean => {
    if (isDirtyRef.current) {
      pendingActionRef.current = onProceed;
      setIsModalOpen(true);
      return false;
    }
    return true;
  }, []); // No dependencies - always stable, uses ref for latest isDirty

  /**
   * Allow subsequent navigation to proceed without triggering the guard.
   * Call this after a successful save operation before navigating away.
   */
  const allowNavigation = useCallback(() => {
    allowNavigationRef.current = true;
  }, []);

  return {
    isModalOpen,
    handleModalDiscard,
    handleModalCancel,
    checkUnsavedChanges,
    allowNavigation,
  };
}
